import * as THREE from 'three'
import type { SceneManager } from './SceneManager'

const MAP_SIZE = 180   // 180×180 px

export type MinimapPickResult = 'pan' | 'zoom' | 'navigate'

export class MinimapRenderer {
  private _sm: SceneManager
  private _orthoCamera: THREE.OrthographicCamera
  private _renderTarget: THREE.WebGLRenderTarget
  private _dirty = true
  private _lastRender = 0
  private _ctx: CanvasRenderingContext2D
  private _cachedImageData: ImageData | null = null

  // 小地图覆盖的世界范围（米）
  worldRange = 200

  constructor(sm: SceneManager, canvas: HTMLCanvasElement) {
    this._sm = sm
    this._ctx = canvas.getContext('2d')!

    this._orthoCamera = new THREE.OrthographicCamera(
      -this.worldRange / 2, this.worldRange / 2,
      this.worldRange / 2, -this.worldRange / 2,
      0.1, 2000
    )
    this._orthoCamera.position.set(0, 500, 0)
    this._orthoCamera.lookAt(0, 0, 0)

    this._renderTarget = new THREE.WebGLRenderTarget(MAP_SIZE, MAP_SIZE)
  }

  markDirty() {
    this._dirty = true
  }

  tick(time: number) {
    if (this._dirty && time - this._lastRender > 500) {
      this._render()
      this._dirty = false
      this._lastRender = time
    } else if (this._cachedImageData) {
      // 底图不变时，每帧仍需重绘以更新视角框位置
      this._ctx.putImageData(this._cachedImageData, 0, 0)
      this._drawViewportRect()
    }
  }

  /** 当前主相机视锥在 minimap 画布上的矩形（与描边一致） */
  getViewportRectCanvas(): { x: number; y: number; w: number; h: number } {
    const cam = this._sm.camera
    const fov = cam.fov * Math.PI / 180
    const aspect = cam.aspect
    const dist = cam.position.y

    const halfH = dist * Math.tan(fov / 2)
    const halfW = halfH * aspect

    const cx = cam.position.x
    const cz = cam.position.z

    const toCanvas = (worldX: number, worldZ: number) => ({
      x: (worldX / this.worldRange + 0.5) * MAP_SIZE,
      y: (worldZ / this.worldRange + 0.5) * MAP_SIZE
    })

    const tl = toCanvas(cx - halfW, cz - halfH)
    const w = (halfW * 2 / this.worldRange) * MAP_SIZE
    const h = (halfH * 2 / this.worldRange) * MAP_SIZE

    return { x: tl.x, y: tl.y, w, h }
  }

  /**
   * 四角约 handlePx 为缩放把手；框内为平移主视角；框外为点击导航到该处。
   */
  pickMinimapInteraction(cx: number, cy: number, handlePx = 10): MinimapPickResult {
    const r = this.getViewportRectCanvas()
    const h = Math.min(handlePx, Math.max(4, r.w / 4, r.h / 4))
    const corners = [
      { x: r.x, y: r.y },
      { x: r.x + r.w - h, y: r.y },
      { x: r.x, y: r.y + r.h - h },
      { x: r.x + r.w - h, y: r.y + r.h - h },
    ]
    for (const c of corners) {
      if (cx >= c.x && cx <= c.x + h && cy >= c.y && cy <= c.y + h) return 'zoom'
    }
    if (cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h) return 'pan'
    return 'navigate'
  }

  private _render() {
    const renderer = this._sm.renderer
    const oldTarget = renderer.getRenderTarget()
    const oldClearColor = renderer.getClearColor(new THREE.Color())
    const oldClearAlpha = renderer.getClearAlpha()

    renderer.setRenderTarget(this._renderTarget)
    renderer.setClearColor(0x1a2535, 1)
    renderer.clear()
    renderer.render(this._sm.scene, this._orthoCamera)

    // 读取像素到 canvas
    const pixels = new Uint8Array(MAP_SIZE * MAP_SIZE * 4)
    renderer.readRenderTargetPixels(this._renderTarget, 0, 0, MAP_SIZE, MAP_SIZE, pixels)

    // 恢复原渲染目标
    renderer.setRenderTarget(oldTarget)
    renderer.setClearColor(oldClearColor, oldClearAlpha)

    // 将像素写入 canvas（注意 WebGL 坐标是从底部开始的，需要翻转）
    const imageData = this._ctx.createImageData(MAP_SIZE, MAP_SIZE)
    for (let y = 0; y < MAP_SIZE; y++) {
      for (let x = 0; x < MAP_SIZE; x++) {
        const srcIdx = ((MAP_SIZE - 1 - y) * MAP_SIZE + x) * 4
        const dstIdx = (y * MAP_SIZE + x) * 4
        imageData.data[dstIdx] = pixels[srcIdx]
        imageData.data[dstIdx + 1] = pixels[srcIdx + 1]
        imageData.data[dstIdx + 2] = pixels[srcIdx + 2]
        imageData.data[dstIdx + 3] = pixels[srcIdx + 3]
      }
    }
    this._ctx.putImageData(imageData, 0, 0)
    this._cachedImageData = imageData

    // 绘制视角框
    this._drawViewportRect()
  }

  private _drawViewportRect() {
    const r = this.getViewportRectCanvas()
    this._ctx.strokeStyle = 'rgba(255,255,255,0.8)'
    this._ctx.lineWidth = 1.5
    this._ctx.strokeRect(r.x, r.y, r.w, r.h)
  }

  /** 将小地图点击坐标（canvas 像素坐标）转换为世界坐标 */
  canvasToWorld(cx: number, cy: number): { x: number; z: number } {
    const x = (cx / MAP_SIZE - 0.5) * this.worldRange
    const z = (cy / MAP_SIZE - 0.5) * this.worldRange
    return { x, z }
  }

  dispose() {
    this._renderTarget.dispose()
  }
}
